import * as Actions from '../actions/commentActions';
import Comment from '../../entities/comment';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type CommentAction = { 
    type: string; 
    comment: Comment | null; 
    commentId: string | Comment; 
    partial: boolean; 
    lastFetch: number; 
    comments: Comment[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Comment> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Comment[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Comment> | null, firmId: string, userId: string, updates?: Comment[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'comment', 'comments', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Comment>} data
 * @param {Comment[]} [updates]
 * @returns {ArrayMap<Comment>}
 */
function updateStorage(data: ArrayMap<Comment>, updates?: Comment[]): ArrayMap<Comment> {
	if (logging) console.debug('⮂ commentReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Comment[]} [updates]
 * @returns {ArrayMap<Comment>}
 */
function updateStorageNoReturn(updates?: Comment[]): void {
	if (logging) console.debug('⮂ commentReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Comment[]} [data]
 * @returns {ArrayMap<Comment>}
 */
function createState(data?: Comment[] | ArrayMap<Comment>): ArrayMap<Comment> {
	if (!data) return ArrayMapRegistry.clone<Comment>('comment', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Comment>('comment', data.map(e => e));
    else return ArrayMapRegistry.clone<Comment>('comment', data);
}

function comments(state: ArrayMap<Comment>, action: CommentAction): ArrayMap<Comment> {
	if (!state) state = createState();

	let st: ArrayMap<Comment>;

	switch (action.type) {
		// action.comment - the new value of comment with id action.id
		// action.commentId - id of the target, used if new value is null
		case Actions.COMMENT_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ commentReducer: FETCH_ONE_SUCCESS', action);

			if (action.comment === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.commentId)) {
				st = state.clone();
				st.set(action.comment);
				// state.clear();

				updateStorageNoReturn([action.comment]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.comments
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.comments
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.comments
			 !partial, !stateEmpty, fetchEmpty	-> D return comments in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge comments in state newer than those in action.comments
		 */
		case Actions.COMMENT_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.comments.length === 0;
			const fetched = action.comments.map(comment => new Comment({...comment, persisted: true}));

			if (logging) console.debug(`⮂ commentReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} comments with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ commentReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ commentReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Comment> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Comment[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(comment => {
					const current = st.get(comment.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && comment.status === 'D') {
							// Remove it from state
							toUpdate.push(comment);
							st.delete(comment);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (comment.addedOn > current.addedOn) {
								toUpdate.push(comment);
								st.set(comment);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (comment.status !== 'D') {
							toUpdate.push(comment);
							st.set(comment);
						}
						else if (logging) console.debug('⮂ commentReducer: partialMerge: skipping deleted comment not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ commentReducer: empty: comments', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ commentReducer: fullMerge: comments', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localComment => {
                    // If this comment has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localComment.persisted) {
                        const fetchedComment = st.get(localComment.id);
                        if (!fetchedComment || localComment.addedOn > fetchedComment.addedOn) st.set(localComment);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.COMMENT_FETCH_ONE_FAILURE:
			console.error('COMMENT_FETCH_ONE_FAILURE', action.commentId, action.err);

			// No update to storage required
			return state;

		case Actions.COMMENT_FETCH_FAILURE:
			console.error('COMMENT_FETCH_FAILURE', action.commentId, action.err);

			// No update to storage required
			return state;

		case Actions.COMMENT_RESTORE: {
		    let comment = new Comment({...action.comment});
            st = state.clone();

            const existing = st.get(comment.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (comment.addedOn >= existing.addedOn) {
                    st.set(comment);
                    return updateStorage(st, [comment]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (comment.status === 'D') return state;

                st.set(comment);
                return updateStorage(st, [comment]);
            }
        }
            
		case Actions.COMMENT_SAVE: {
		    // console.debug('⮂ COMMENT_SAVE reducer', action.comment?.id);
		    
			// clone the comment
			let comment = new Comment({...action.comment});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new comment in the state
			// If this is a create, add to the queue
			const _comment = st.get(comment.id);

			if (!_comment) st.set(comment);
			else {
				if (comment.addedOn >= _comment.addedOn) st.set(comment);
			}

			return updateStorage(st, [comment]);
		}

		case Actions.COMMENT_SAVE_MANY: {
		    if (logging) console.debug('⮂ COMMENT_SAVE_MANY reducer', action.comments.map(comment => comment.id).join(','));
		    
			// clone the comments
			let toProcess = action.comments.map(comment => new Comment(comment));

			st = state.clone();
			// state.clear();

			const updates: Comment[] = [];

			toProcess.forEach(comment => {
				// Add or update the new comment in the state
				// If this is a create, add to the queue
				const existing = st.get(comment.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (comment.status !== 'D') {
						st.push(comment);
						updates.push(comment);
					}
					else if (logging) console.debug('⮂ COMMENT_SAVE_MANY: skipping deleted comment not in state');
				}
				else {
					if (comment.addedOn >= existing.addedOn) {
						st.set(comment);
						updates.push(comment);
					}
					else if (logging) console.debug('⮂ Skipping, existing comment had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.COMMENT_REMOVE: {
			// clone the comment
			const comment = new Comment({...action.comment});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(comment);

			return updateStorage(st, [comment]);
		}

		case Actions.COMMENT_RESTORE_FAILURE:
		case Actions.COMMENT_SAVE_FAILURE: {
		    console.warn('COMMENT_SAVE_FAILURE reducer', action.comment?.id);
		    if (!action.comment?.id) {
				console.warn('COMMENT_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const comment = ensure(Comment, action.comment);
			if (!comment) {
				console.error('Could not find comment after call to save');
				return state;
			}

			st = state.clone();

			const _commentRef = state.get(comment.id);

			if (_commentRef && (_commentRef.addedOn === comment.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _comment = new Comment({..._commentRef});
				_comment.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_comment.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('comment', _comment).then(r => {
						if (r.isErr()) console.error(`⮂ commentReducer: failed to save comment with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ commentReducer: saved comment with fatalError info');
					}).catch(err => console.error(`⮂ commentReducer: failed to save comment with fatalError info: ${err.message}`));

				st.set(_comment);
			}

			return updateStorage(st);
		}

		case Actions.COMMENT_SAVE_MANY_FAILURE: {
		    console.warn('COMMENT_SAVE_MANY_FAILURE reducer', action.comments.map(comment => comment.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.COMMENT_RESTORE_SUCCESS:
		case Actions.COMMENT_SAVE_SUCCESS: {
			if (!action.comment?.id) {
				console.warn('COMMENT_SAVE_SUCCESS reducer');
                return state;
			}

			const commentRef = state.get(action.comment?.id);
			if (!commentRef) {
				console.warn('Could not find comment after call to save');
                return state;
			}

			st = state.clone();

			if (action.comment.addedOn >= commentRef.addedOn) {
				st.set(action.comment);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const comment = new Comment({...commentRef, persisted: true});
				st.set(comment);
			}

			return updateStorage(st, [action.comment]);
		}

		case Actions.COMMENT_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ COMMENT_SAVE_MANY_SUCCESS reducer', action.comments.map(comment => comment.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const comments: Comment[] = [];

			action.comments.forEach(e => {
				const commentRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!commentRef) console.warn('Could not find comment after call to save');
				else if (e.addedOn >= commentRef.addedOn) {
				    st.set(e);
				    comments.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const comment = new Comment({...commentRef, persisted: true});
					st.set(comment);
					comments.push(comment);
				}
			});

			return updateStorage(st, comments);
		}

		case Actions.COMMENT_REMOVE_FAILURE: {
		    console.warn('COMMENT_REMOVE_FAILURE action', action.comment?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.COMMENT_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.COMMENT_RESTORE_MANY: {
			if (logging) console.debug('⮂ COMMENT_RESTORE_MANY reducer', action.comments.map(comment => comment.id).join(','));

			const toProcess = action.comments.map(comment => new Comment({...comment}));
			st = state.clone();

			const updates: Comment[] = [];

			toProcess.forEach(comment => {
				const existing = st.get(comment.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (comment.addedOn >= existing.addedOn) {
						st.set(comment);
						updates.push(comment);
					}
					else if (logging) console.debug('⮂ COMMENT_RESTORE_MANY: skipping older version of comment');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (comment.status !== 'D') {
						st.set(comment);
						updates.push(comment);
					}
					else if (logging) console.debug('⮂ COMMENT_RESTORE_MANY: skipping deleted comment not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.COMMENT_REMOVE_MANY: {
			if (logging) console.debug('⮂ COMMENT_REMOVE_MANY reducer', action.comments.map(comment => comment.id).join(','));

			const toProcess = action.comments.map(comment => new Comment({...comment}));
			st = state.clone();

			toProcess.forEach(comment => {
				st.delete(comment);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default comments;